import React, { JSX, useContext, useEffect, useState } from "react";
import { startCase } from "lodash";
import { TooltipProps } from "recharts";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { NetworkComparisonFilters, NetworkComparisonFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import { QHPFilters, QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters";
import { MedicareFilters } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { EffectivenessAndEfficiency, Ghosts, HospitalAndPcpCount, ProviderCount, ProviderOverlap, ProvidersByNetwork, TotalProviderCount } from "@app/overviews/network-benchmarking-dashboard/tiles";
import { ProviderCountTypeEnum, TotalProviderCountData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { PageHeader } from "@app/shared";
import { DashboardTile } from "@app/shared/qes-standards";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { printNumberAsString } from "@app/shared/waffles";

const BenchmarkingDashboardDefaultView = (): JSX.Element => {
  const [providersByNetworkHeight, setProvidersByNetworkHeight] = useState<number>(0);
  const [providerOverlapHeight, setProviderOverlapHeight] = useState<number>(0);

  const benchmarkingContext = useContext(BenchmarkingContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);
  const featureFlagContext = useContext(FeatureFlagContext);

  useEffect(() => {
    if (!benchmarkingContext.providersByNetworkData?.length) {
      return;
    }

    const resizeObserver = new ResizeObserver((event) => {
      setProvidersByNetworkHeight((event[0]?.target?.firstChild as HTMLDivElement)?.clientHeight);
    });

    const providersByNetworkContainer = document.getElementById("benchmarking-dashboard__providers-by-network--container");

    if (!!providersByNetworkContainer) {
      resizeObserver.observe(providersByNetworkContainer);
    }

    return () => resizeObserver.disconnect();
  }, [benchmarkingContext.providersByNetworkData]);

  useEffect(() => {
    if (!providersByNetworkHeight) {
      return;
    }

    const resizeObserver = new ResizeObserver((event) => {
      const providerOverlapHeaderHeight = (event[0]?.target?.firstChild?.firstChild?.firstChild as HTMLDivElement)?.clientHeight;
      // obtain the calculated provider overlap height by getting the total height of the providers by network container,
      // then subtracting the provider overlap header height and the margins from it
      const calculatedProviderOverlapHeight = providersByNetworkHeight - providerOverlapHeaderHeight - 24;
      setProviderOverlapHeight(calculatedProviderOverlapHeight);
    });

    const providerOverlapContainer = document.getElementById("benchmarking-dashboard--provider-overlap--container");

    if (!!providerOverlapContainer) {
      resizeObserver.observe(providerOverlapContainer);
    }

    return () => resizeObserver.disconnect(); 
  }, [providersByNetworkHeight]);

  const customTooltip = (
    props: TooltipProps<ValueType, string | number>,
    titleKey: string,
    round?: number,
  ): JSX.Element => {
    // this accounts for the chart tooltip for all charts except for the ghost tile, which is built in the ghost component itself
    if (props.payload?.length) {
      const order: number[] = Array.from(Array(props.payload.length)).map((_, index) => index).reverse();

      const tooltipItems: JSX.Element[] = order.map((index) => {
        const payloadItem = props.payload[index];

        const payloadValue = payloadItem.value as number;
        const displayValue = !!payloadValue ? (
          !!round ? payloadValue.toFixed(round) : printNumberAsString(payloadValue, "0")
        ) : 0;

        return (
          <p className="mb-0" key={index}>
            <span>{startCase(payloadItem.name as string)}:</span> {displayValue}
          </p>
        );
      });

      let parentOrganizationName;
      let networkName;

      if (!!benchmarkingFiltersContext.selectedNetworks?.length) {
        const network = benchmarkingFiltersContext.selectedNetworks.find(x => x.contractId === props.payload[0].payload[titleKey]);

        if (!!network) {
          parentOrganizationName = network.parentOrganization;

          switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
            case BenchmarkingLineOfBusinessEnum.Medicare:
              networkName = props.payload[0].payload[titleKey];
              break;
            case BenchmarkingLineOfBusinessEnum.Marketplace:
              networkName = (network as QHPNetwork).networkName;
              break;
            default:
              break;
          }
        }
      }

      return (
        <div className="qes-dashboard__custom-tooltip">
          {!!parentOrganizationName && (
            <p className="qes-dashboard__tooltip-title mb-0">{parentOrganizationName}</p>
          )}
          <p className="qes-dashboard__tooltip-title mb-0">{networkName}</p>
          {tooltipItems}
        </div>
      );
    }
  }

  const getBenchmarkingFilters = () => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Medicare:
        return (
          <MedicareFilters />
        );
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        return (
          <QHPFilters />
        );
      default:
        return (
          <MedicareFilters />
        );
    }
  }
  
  const getTotalProviderCount = (contractId: string) => {
    const totalProviderCount: TotalProviderCountData = benchmarkingContext.totalProviderCountData.find(x => x.contractId === contractId);

    return totalProviderCount?.providerCount;
  }

  const getCustomXAxis = () => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        const CustomXAxisTick = ({ x, y, payload }: { x: number, y: number, payload: { value: string } }): JSX.Element => {
          if (!!payload?.value) {
            const contract: QHPNetwork = benchmarkingFiltersContext.selectedNetworks.find(x => x.contractId === payload.value) as QHPNetwork;

            if (!!contract) {
              const splitContract: string[] = contract.contractId.split("_");

              return (
                <g transform={`translate(${x-5},${y-10}) rotate(-45)`} textAnchor="end">
                  <text x={0} y={0} dy={16} style={{ fontSize: "12px" }}>
                    <tspan className="text-muted" textAnchor="end" x="0">
                      {splitContract[0]}
                    </tspan>
                    <tspan textAnchor="end" x="0" dy="15">
                      {splitContract[1]}
                    </tspan>
                  </text>
                </g>
              );
            }
          }
        }

        return CustomXAxisTick;
      case BenchmarkingLineOfBusinessEnum.Medicare:
        const CustomXAxisTickSingle = ({ x, y, payload }: { x: number, y: number, payload: { value: string } }): JSX.Element => {
          if (!!payload?.value) {
            const contract: QHPNetwork = benchmarkingFiltersContext.selectedNetworks.find(x => x.contractId === payload.value) as QHPNetwork;

            if (!!contract) {
              return (
                <g transform={`translate(${x - 5},${y}) rotate(-45)`} textAnchor="end">
                  <text x={0} y={0} dy={16} style={{ fontSize: "14px" }}>
                    <tspan textAnchor="end" x="0">
                      {contract.contractId}   
                    </tspan>
                  </text>
                </g>
              );
            }
          }
        }

        return CustomXAxisTickSingle;
      default:
        return undefined;
    }
  }

  const getCustomYAxis = () => {
    switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
      case BenchmarkingLineOfBusinessEnum.Marketplace:
        const CustomYAxisTick = ({ x, y, payload }: { x: number, y: number, payload: { value: string } }): JSX.Element => {
          if (!!payload?.value) {
            const contract: QHPNetwork = benchmarkingFiltersContext.selectedNetworks.find(x => x.contractId === payload.value) as QHPNetwork;

            if (!!contract) {
              const splitContract: string[] = contract.contractId.split("_");

              return (
                <g>
                  <text x={x} y={y-3} textAnchor="end" style={{ fontSize: "12px" }}>
                    <tspan className="text-muted" textAnchor="start" x="0">
                      {splitContract[0]}
                    </tspan>
                    <tspan textAnchor="start" x="0" dy="15">
                      {splitContract[1]}
                    </tspan>
                  </text>
                </g>
              );
            }
          }
        }

        return CustomYAxisTick;
      default:
        return undefined;
    }
  }

  const drillInEnabled = networkComparisonFiltersContext.displayedNetworkComparisonFilters?.specialtyGroupId !== 0 || featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_BENCHMARKING_ALL_SPECIALTY_DRILL_IN);
  
  return (
    <React.Fragment>
      <PageHeader disableHR={true} title="Competitive Insights Dashboard" />

      {getBenchmarkingFilters()}

      <div className="row">
        <TotalProviderCount
          colorMap={benchmarkingContext.hContractColorMap}
          customTooltip={customTooltip}
          customYAxisTick={getCustomYAxis()}
          data={benchmarkingContext.totalProviderCountData}
          loading={benchmarkingContext.totalProviderCountDataLoading}
        />
        <HospitalAndPcpCount
          colorMap={benchmarkingContext.hContractColorMap}
          customTooltip={customTooltip}
          data={benchmarkingContext.hospitalAndPcpCountData}
          loading={benchmarkingContext.hospitalAndPcpCountDataLoading}
        />
      </div>

      <NetworkComparisonFilters />

      {!!benchmarkingFiltersContext.displayedNetworkFilters.length && (
        <React.Fragment>
          <div className="row">
            <div className="col-md-6 d-flex flex-column justify-content-between">
              <div className="row" id="benchmarking-dashboard__providers-by-network--container">
                <ProvidersByNetwork
                  canDrillIn={drillInEnabled}
                  customTooltip={customTooltip}
                  customYAxisTick={getCustomYAxis()}
                  data={benchmarkingContext.providersByNetworkData}
                  loading={benchmarkingContext.providersByNetworkDataLoading}
                  onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                  selectedNetwork={networkComparisonFiltersContext.displayedNetworkComparisonFilters?.primaryNetwork?.contractId}
                />
              </div>

              <div className="row">
                <EffectivenessAndEfficiency
                  canDrillIn={drillInEnabled}
                  chartType={"effectiveness"}
                  customTooltip={customTooltip}
                  customXAxisTick={getCustomXAxis()}
                  data={benchmarkingContext.effectivenessAndEfficiencyData}
                  getTotalProviderCount={getTotalProviderCount}
                  loading={benchmarkingContext.effectivenessAndEfficiencyDataLoading}
                  onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                  selectedNetwork={networkComparisonFiltersContext.displayedNetworkComparisonFilters?.primaryNetwork?.contractId}
                />
                <EffectivenessAndEfficiency
                  canDrillIn={drillInEnabled}
                  chartType={"efficiency"}
                  customTooltip={customTooltip}
                  customXAxisTick={getCustomXAxis()}
                  data={benchmarkingContext.effectivenessAndEfficiencyData}
                  getTotalProviderCount={getTotalProviderCount}
                  loading={benchmarkingContext.effectivenessAndEfficiencyDataLoading}
                  onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                  selectedNetwork={networkComparisonFiltersContext.displayedNetworkComparisonFilters?.primaryNetwork?.contractId}
                />
              </div>

              <div className="row">
                <Ghosts
                  canDrillIn={drillInEnabled}
                  customYAxisTick={getCustomYAxis()}
                  data={benchmarkingContext.ghostData}
                  loading={benchmarkingContext.ghostDataLoading}
                  onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                  selectedLineOfBusiness={benchmarkingFiltersContext.selectedLineOfBusiness}
                  selectedNetwork={networkComparisonFiltersContext.displayedNetworkComparisonFilters?.primaryNetwork?.contractId}
                  selectedNetworks={benchmarkingFiltersContext.selectedNetworks}
                  selectedPcaLineOfBusiness={networkComparisonFiltersContext.selectedPcaLineOfBusiness}
                />
              </div>
            </div>

            <div className="col-md-6">
              <div className={`row${benchmarkingFiltersContext.selectedNetworks.length > 2 ? " h-100" : ""}`} id="benchmarking-dashboard--provider-overlap--container">
                <DashboardTile
                  loading={benchmarkingContext.providerOverlapDataLoading || !providerOverlapHeight}
                  title="Provider Overlap"
                  subtitle={`This is the comparison of your network to your competitor's networks.${drillInEnabled ? " Each element below can be clicked to reveal additional details." : ""}`}
                  widthClass="col-md-12"
                >
                  <div className="d-flex flex-column justify-content-between h-100">
                    <ProviderOverlap
                      canDrillIn={drillInEnabled}
                      data={benchmarkingContext.providerOverlapData}
                      onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                      providerOverlapHeight={providerOverlapHeight}
                    />

                    {benchmarkingFiltersContext.selectedNetworks.length > 2 && (
                      <React.Fragment>
                        <ProviderCount
                          canDrillIn={drillInEnabled}
                          customTooltip={customTooltip}
                          data={benchmarkingContext.providerOverlapData?.inNetworkCounts}
                          onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                          providerCountType={ProviderCountTypeEnum.InNetwork}
                        />

                        <ProviderCount
                          canDrillIn={drillInEnabled}
                          customTooltip={customTooltip}
                          data={benchmarkingContext.providerOverlapData?.outNetworkCounts}
                          onDashboardDetailsViewClick={benchmarkingContext.onDashboardDetailsViewClick}
                          providerCountType={ProviderCountTypeEnum.OutOfNetwork}
                        />
                      </React.Fragment>
                    )}
                  </div>
                </DashboardTile>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default BenchmarkingDashboardDefaultView;